import { useEffect, useState, useMemo } from 'react';
import axiosInstance from '../api/axiosInstance';
import { DomainKey } from '../config/domains';

const baseUrl = process.env.REACT_APP_API_URL;

interface PrecheckPos {
  id: number;
  company: string;
  contactPerson: string;
  email: string;
  additionalServices: string[];
  sourceDomain: DomainKey;
  createdAt: string;
}

interface UsePrechecksPOSParams {
  search?: string;
  page?: number;
  limit?: number;
}

const usePrechecksPOS = (params: UsePrechecksPOSParams = {}) => {
  const [allPrecheckPoses, setAllPrecheckPoses] = useState<PrecheckPos[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { search = '', page = 1, limit = 10 } = params;

  // Fetch all precheck POS once on mount
  useEffect(() => {
    const fetchPrecheckpos = async () => {
      try {
        const res = await axiosInstance.get(`${baseUrl}/admin/precheck-pos`);
        const sortedPrecheckPoses = res.data.sort(
          (a: PrecheckPos, b: PrecheckPos) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setAllPrecheckPoses(sortedPrecheckPoses);
      } catch (error) {
        setError('Error fetching POS Pre-checks.');
      } finally {
        setLoading(false);
      }
    };

    fetchPrecheckpos();
  }, []);

  // Client-side filtering and pagination
  const { precheckPoses, totalCount, totalPages } = useMemo(() => {
    // Filter precheck POS based on search term (company name)
    let filteredPrecheckPoses = allPrecheckPoses;

    if (search.trim()) {
      filteredPrecheckPoses = allPrecheckPoses.filter(
        (precheckPos: PrecheckPos) =>
          precheckPos.company?.toLowerCase().includes(search.toLowerCase()) ||
          precheckPos.contactPerson?.toLowerCase().includes(search.toLowerCase()) ||
          precheckPos.email?.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Calculate pagination
    const totalCount = filteredPrecheckPoses.length;
    const totalPages = Math.ceil(totalCount / limit);

    // Get precheck POS for current page
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedPrecheckPoses = filteredPrecheckPoses.slice(startIndex, endIndex);

    return {
      precheckPoses: paginatedPrecheckPoses,
      totalCount,
      totalPages,
    };
  }, [allPrecheckPoses, search, page, limit]);

  return {
    precheckPoses,
    totalCount,
    totalPages,
    currentPage: page,
    loading,
    error,
  };
};

export default usePrechecksPOS;
